// src/components/modals/EditSMSCampaignModal.jsx
import { useState, useEffect } from 'react'
import { X, MessageCircle, Users, AlertCircle, CheckCircle, Edit } from 'lucide-react'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import ButtonSpinner from '../ui/ButtonSpinner'
import SMSCampaignService from '../../services/smsCampaignService'
import { SMSCampaignModel, SMS_CAMPAIGN_STATUS } from '../../utils/smsCampaignModel'

const EditSMSCampaignModal = ({ isOpen, onClose, onSuccess, campaign, customerGroups }) => {
  const [formData, setFormData] = useState({
    title: '',
    customerGroup: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [toast, setToast] = useState(null)

  // Initialize form data when campaign changes
  useEffect(() => {
    if (campaign) {
      setFormData({
        title: campaign.title || '',
        customerGroup: campaign.customerGroup?.id || ''
      })
    }
  }, [campaign])

  if (!isOpen || !campaign) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (isSubmitting) return

    try {
      setIsSubmitting(true)
      setError('')

      // Basic validation
      if (!formData.title.trim()) {
        setError('Campaign title is required')
        return
      }

      if (!formData.customerGroup) {
        setError('Customer group is required')
        return
      }

      // Check if campaign can be edited
      if (!SMSCampaignModel.canEdit(campaign.status)) {
        setError('This campaign cannot be edited in its current status')
        return
      }

      // Prepare update data (only send changed fields)
      const updateData = {}
      if (formData.title !== campaign.title) {
        updateData.title = formData.title
      }
      if (formData.customerGroup !== campaign.customerGroup?.id) {
        updateData.customerGroup = formData.customerGroup
      }

      if (Object.keys(updateData).length === 0) {
        setError('No changes detected')
        return
      }

      console.log('📱 Updating SMS campaign:', campaign.id, updateData)
      const response = await SMSCampaignService.updateSMSCampaign(campaign.id, updateData)

      if (response.success) {
        setToast({
          message: 'SMS campaign updated successfully',
          type: 'success'
        })
        
        // Close modal after a brief delay
        setTimeout(() => {
          onSuccess()
        }, 1500)
      } else {
        setError(response.message || 'Failed to update SMS campaign')
      }

    } catch (error) {
      console.error('Failed to update SMS campaign:', error)
      setError(error.message || 'Failed to update SMS campaign')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    
    // Clear error when user starts typing
    if (error) setError('')
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setError('')
      setToast(null)
      onClose()
    }
  }

  const getSelectedCustomerGroup = () => {
    return (customerGroups || []).find(group => group._id === formData.customerGroup)
  }

  const getOriginalCustomerGroup = () => {
    return (customerGroups || []).find(group => group._id === campaign.customerGroup?.id)
  }

  const hasChanges = () => {
    return (
      formData.title !== campaign.title ||
      formData.customerGroup !== campaign.customerGroup?.id
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200">
          <div className="flex items-center space-x-3">
            
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit SMS Campaign</h2>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mx-6 mt-4 p-4 rounded-lg flex items-center space-x-2 ${
            toast.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
            {toast.type === 'success' ? 
              <CheckCircle className="h-5 w-5" /> : 
              <AlertCircle className="h-5 w-5" />
            }
            <span>{toast.message}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Campaign Status Warning */}
          {!SMSCampaignModel.canEdit(campaign.status) && (
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-800">
                    This campaign is in "{campaign.status}" status and cannot be fully edited. 
                    Only limited changes may be allowed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Current Campaign Info */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Current Campaign</h4>
            <div className="space-y-2 text-xs text-gray-600">
              <div><strong>ID:</strong> {campaign.id}</div>
              <div><strong>Status:</strong> 
                <span className={`ml-2 px-2 py-1 rounded text-xs ${campaign.statusConfig.color}`}>
                  {campaign.statusConfig.icon} {campaign.statusConfig.label}
                </span>
              </div>
              <div><strong>Created:</strong> {campaign.createdAtFormatted}</div>
              <div><strong>Original Group:</strong> {getOriginalCustomerGroup()?.name}</div>
            </div>
          </div>

          {/* Campaign Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Body *
            </label>
            <textarea
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Enter your SMS message..."
              maxLength="200"
              disabled={isSubmitting || !SMSCampaignModel.canEdit(campaign.status)}
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none"
            />
            <p className="text-xs text-gray-500 mt-1">
              {formData.title.length}/200 characters
            </p>
          </div>

          {/* Customer Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Customer Group *
            </label>
            <select
              name="customerGroup"
              value={formData.customerGroup}
              onChange={handleInputChange}
              disabled={isSubmitting || !SMSCampaignModel.canEdit(campaign.status)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Select customer group...</option>
              {(customerGroups || []).map(group => (
                <option key={group._id} value={group._id}>
                  {group.name} ({group.customers?.length || 0} customers)
                </option>
              ))}
            </select>
          </div>

          {/* Selected Customer Group Info */}
          {formData.customerGroup && (
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Users className="h-4 w-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-900">
                    {getSelectedCustomerGroup()?.name}
                    {formData.customerGroup !== campaign.customerGroup?.id && 
                      <span className="ml-2 text-xs text-green-700">(Changed)</span>
                    }
                  </h4>
                  <p className="text-xs text-green-700 mt-1">
                    {getSelectedCustomerGroup()?.description}
                  </p>
                  <p className="text-xs text-green-600 mt-2 flex items-center">
                    <Users className="h-3 w-3 mr-1" />
                    {getSelectedCustomerGroup()?.customers?.length || 0} customers will receive this campaign
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Changes Summary */}
          {hasChanges() && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-medium text-blue-700 mb-2">Changes Detected</h4>
              <ul className="text-xs text-blue-600 space-y-1">
                {formData.title !== campaign.title && (
                  <li>• Title: "{campaign.title}" → "{formData.title}"</li>
                )}
                {formData.customerGroup !== campaign.customerGroup?.id && (
                  <li>• Customer Group: "{getOriginalCustomerGroup()?.name}" → "{getSelectedCustomerGroup()?.name}"</li>
                )}
              </ul>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !formData.title.trim() || !formData.customerGroup || !hasChanges()}
              className="bg-green-600 hover:bg-green-700 text-white min-w-[100px]"
            >
              {isSubmitting ? <ButtonSpinner size="sm" /> : 'Update Campaign'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditSMSCampaignModal